import { useCallback, useEffect } from "react";

import { useForm } from "react-hook-form";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { automationService } from "../services/automationService";
import {
  buildAutomationFormDefaultValues,
  toAutomationConfig,
  toAutomationFormValues,
  type AutomationFormValues,
} from "../transformers/automationFormTransformers";
import type { AutomationConfig, AutomationContext, AutomationId } from "../types";

export interface UseAutomationFormParams {
  automationId: AutomationId;
  context: AutomationContext;
  defaultTemplateId?: string;
  onSaved?: (config: AutomationConfig) => void;
}

export interface UseAutomationFormResult {
  automationId: AutomationId;
  control: ReturnType<typeof useForm<AutomationFormValues>>["control"];
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submit: () => Promise<void>;
  formErrors: ReturnType<typeof useForm<AutomationFormValues>>["formState"]["errors"];
  isInitialLoading: boolean;
  isSubmitting: boolean;
  hasSubmitError: boolean;
  selectedTemplateId: string;
  setMessageTemplateId: (templateId: string) => void;
}

export function useAutomationForm(params: UseAutomationFormParams): UseAutomationFormResult {
  const {
    automationId,
    context,
    defaultTemplateId,
    onSaved,
  } = params;

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm<AutomationFormValues>({
    defaultValues: buildAutomationFormDefaultValues({
      automationId,
      defaultTemplateId,
    }),
  });

  useEffect(() => {
    register("messageTemplateId", {
      required: "Selecione um template de mensagem.",
    });
  }, [register]);

  const selectedTemplateId = watch("messageTemplateId");

  const setMessageTemplateId = useCallback(
    (templateId: string) => {
      setValue("messageTemplateId", templateId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const { run: loadExistingConfig, loading: isInitialLoading } = useAsyncTask<
    [AutomationId],
    AutomationConfig | null
  >({
    task: useCallback(
      async (id) => {
        const existingConfig = await automationService.getConfigByAutomationId({
          context,
          automationId: id,
        });

        if (!existingConfig) {
          return null;
        }

        const nextValues = toAutomationFormValues(existingConfig);
        (
          Object.entries(nextValues) as [
            keyof AutomationFormValues,
            AutomationFormValues[keyof AutomationFormValues],
          ][]
        ).forEach(([field, value]) => {
          setValue(field, value, { shouldValidate: false });
        });

        return existingConfig;
      },
      [context, setValue],
    ),
  });

  const { run: saveConfigTask, loading: isSubmitting, status: submitStatus } = useAsyncTask<
    [AutomationFormValues],
    AutomationConfig
  >({
    task: useCallback(
      async (values) => {
        const payload = toAutomationConfig({ automationId, values });
        const savedConfig = await automationService.upsertConfig({
          context,
          config: payload,
        });

        if (onSaved) {
          onSaved(savedConfig);
        }

        return savedConfig;
      },
      [automationId, context, onSaved],
    ),
  });

  const handleSubmit = rhfHandleSubmit(async (values) => {
    await saveConfigTask(values);
  });

  const submit = useCallback(async () => {
    await handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    loadExistingConfig(automationId);
  }, [automationId, loadExistingConfig]);

  return {
    automationId,
    control,
    handleSubmit,
    submit,
    formErrors: errors,
    isInitialLoading,
    isSubmitting,
    hasSubmitError: submitStatus === "error",
    selectedTemplateId,
    setMessageTemplateId,
  };
}
